import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {
    selectGenerationHistoryEnabled,
    selectSelectedWorkspaceId,
    selectUser,
    setSelectedWorkspaceId
} from "../../utils/userSlice";
import {useApiService} from "../../context/ApiServiceContext";
import {useMobile} from "../../context/MobileContext";
import {useNavigate} from "react-router-dom";
import {AxiosResponse} from "axios";
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {toast} from 'react-toastify'; // Import toast
// Import components
import WorkspaceSelector from './components/WorkspaceSelector';
import TemplateCard from './components/TemplateCard';
import TemplateSkeleton from './components/TemplateSkeleton';
import CreationsHomeEntry from './components/CreationsHomeEntry';
import User from "../../models/User";
import Template from "../../models/Template";

// Styled components
const MainContainer = styled(Box)(({theme}) => ({
    minHeight: '100vh',
}));

const ContentContainer = styled(Container)(({theme}) => ({
    padding: theme.spacing(3),
}));

const EmptyState = styled(Box)(({theme}) => ({
    textAlign: 'center',
    padding: theme.spacing(6, 0),
}));

const LoadingContainer = styled(Box)(({theme}) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const TemplateList: React.FC = () => {
    const {t} = useTranslation();
    const {isMobile} = useMobile();
    const generationHistoryEnabled = useSelector(selectGenerationHistoryEnabled);
    const dispatch = useDispatch();
    const user = useSelector(selectUser) as User | null;
    const apiService = useApiService();
    const navigate = useNavigate();

    const [templates, setTemplates] = useState<Template[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPaginationLoading, setIsPaginationLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);

    const observer = useRef<IntersectionObserver | null>(null);
    const TEMPLATES_PER_PAGE = 20;
    const selectedWorkspaceId = useSelector(selectSelectedWorkspaceId) as string | null;

    // Function to load templates from API
    const loadTemplates = useCallback((pageNumber: number, workspaceId: string) => {
        if (!workspaceId) return;

        // Set pagination loading state only for subsequent pages
        if (pageNumber > 1) {
            setIsPaginationLoading(true);
        } else {
             setIsLoading(true); // Set initial loading for page 1
        }

        apiService.api.get<Template[]>(`/workspace/${workspaceId}/templates?perPage=${TEMPLATES_PER_PAGE}&page=${pageNumber}`)
            .then((res: AxiosResponse<Template[]>) => {
                if (pageNumber === 1) {
                    setTemplates(res.data);
                } else {
                    setTemplates(prevTemplates => [...prevTemplates, ...res.data]);
                }

                setHasMore(res.data.length === TEMPLATES_PER_PAGE);
            })
            .catch(error => {
                console.error("Error loading templates:", error);
                // Add translation key "error.templateLoad" to your locales files
                toast.error(t("error.templateLoad") || "Failed to load templates."); // Add toast notification
            })
            .finally(() => {
                setIsLoading(false);
                setIsPaginationLoading(false);
            });
    // Added apiService and t to dependencies
    }, [apiService, t]);


    // Load initial templates when workspace changes
    useEffect(() => {
        if (selectedWorkspaceId) {
            setIsLoading(true); // Ensure loading state is set
            setTemplates([]); // Clear previous templates
            setPage(1); // Reset page number
            setHasMore(true); // Reset hasMore flag
            loadTemplates(1, selectedWorkspaceId);
        } else {
             setIsLoading(false); // If no workspace is selected, stop loading
             setTemplates([]); // Clear templates if no workspace selected
        }
    // Added loadTemplates to dependencies
    }, [selectedWorkspaceId, loadTemplates]);


    // Setup intersection observer for infinite scrolling
    const lastTemplateElementRef = useCallback((node: HTMLDivElement | null) => {
        if (isLoading || isPaginationLoading) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && selectedWorkspaceId) {
                 console.log("Loading next page..."); // Debug log
                setPage(prevPage => {
                    const nextPage = prevPage + 1;
                    loadTemplates(nextPage, selectedWorkspaceId);
                    return nextPage;
                });
            }
        }, {threshold: 0.5}); // Threshold might need adjustment

        if (node) observer.current.observe(node);
    // Added isLoading, isPaginationLoading, hasMore, loadTemplates, selectedWorkspaceId dependencies
    }, [isLoading, isPaginationLoading, hasMore, loadTemplates, selectedWorkspaceId]);

    // Handle workspace selection
    const handleWorkspaceChange = (workspaceId: string) => {
        dispatch(setSelectedWorkspaceId(workspaceId));
    };

    // Handle template click
    const handleTemplateClick = (id: string) => {
        navigate(`/template/${id}`);
    };

    // Render pagination loading skeletons
    const renderPaginationSkeletons = () => {
        return (
            <>
                {Array.from({length: 4}).map((_, index) => (
                    <Grid item
                          xs={12}  // Match TemplateCard breakpoints so the shimmer
                          sm={6}   // is the exact size/position of the real card.
                          md={4}
                          lg={3}
                          key={`pagination-skeleton-${index}`}>
                        <TemplateSkeleton />
                    </Grid>
                ))}
            </>
        );
    };

    return (
        <MainContainer>
            <WorkspaceSelector
                workspaces={user?.workspaces || []}
                selectedWorkspaceId={selectedWorkspaceId}
                onChange={handleWorkspaceChange}
            />

            <ContentContainer maxWidth="xl">
                {/* Mobile only, and the exact inverse of the navbar gating:
                    the header carries the labelled button on desktop, so a row
                    here too would be the same destination twice. On a phone the
                    header has no room for it, so this is the only way in. */}
                {isMobile && generationHistoryEnabled && (
                    <Box sx={{mb: 3}}>
                        <CreationsHomeEntry/>
                    </Box>
                )}

                <Grid container spacing={3}>
                    {/* Initial loading skeletons - Render only when page is 1 and isLoading */}
                    {isLoading && page === 1 && Array.from({length: 8}).map((_, index) => (
                        <Grid item
                              xs={12} // Match TemplateCard breakpoints so the shimmer
                              sm={6}  // matches the real card size/position on mobile.
                              md={4}
                              lg={3}
                              key={`skeleton-${index}`}>
                            <TemplateSkeleton/>
                        </Grid>
                    ))}

                    {/* Render actual templates */}
                    {!isLoading && templates.map((template, index) => {
                        const isLastElement = templates.length === index + 1;
                        return (
                            <Grid
                                item
                                xs={12} // Changed xs to 12 for better mobile stacking initially
                                sm={6}  // Adjusted sm
                                md={4}  // Adjusted md
                                lg={3}  // Kept lg
                                key={template.id}
                                ref={isLastElement ? lastTemplateElementRef : null} // Use null instead of undefined
                            >
                                <TemplateCard
                                    template={template}
                                    onClick={handleTemplateClick}
                                />
                            </Grid>
                        );
                    })}


                    {/* Pagination loading skeletons - Render only when loading next pages */}
                    {isPaginationLoading && renderPaginationSkeletons()}
                </Grid>

                {/* Empty state - Show only when not initially loading and no templates */}
                {!isLoading && templates.length === 0 && (
                    <EmptyState>
                        <Typography variant="h6" color="text.secondary">
                            {t("main.templateList.noTemplates")}
                        </Typography>
                    </EmptyState>
                )}
            </ContentContainer>
        </MainContainer>
    );
};

export default TemplateList;
